import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './LoanApplication.css';
import { Header, SideMenu, TopMenu } from 'maxcu-shared-lib';
import menuItems from '../SideMenu/menuItems';

const LoanApplication = () => {
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        // Set the browser title
        document.title = 'My Offers | MAX Credit Union';
      }, []);
    
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = 4;
    const [isReviewing, setIsReviewing] = useState(false);
    const [loanTypeDisplay, setLoanTypeDisplay] = useState('Loan');


    const [isCollapsed, setIsCollapsed] = useState(false);


  
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
  
    useEffect(() => {
      handleResize(); // Initial check
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    const [formData, setFormData] = useState({
        // Loan Details
        loanTerm: '36',
        interestRate: '4.99',
        monthlyPayment: '',
        requestedAmount: '',
        unsecuredLoanPurpose: '',
        // Page 1
        firstName: '', middleName: '', lastName: '', suffix: '', birthDate: '', ssn: '',
        // Page 2
        addressLine1: '', addressLine2: '', city: '', state: '', zipCode: '',
        // Page 3
        housing: '', housingPayment: '', homePhone: '', mobilePhone: '', workPhone: '', email: '', preferredContact: '',
        // Page 4
        incomeSource: '', occupation: '', incomeFrequency: '', incomeAmount: '', employmentYears: '', employmentMonths: '',
        additionalIncomeSource: '', additionalIncomeType: '', additionalIncomeFrequency: '', additionalIncomeAmount: '',
        loan_type: 'Personal Loan'
    });

    useEffect(() => {
        // Get the loan details from navigation state if available
        if (location.state) {
            const { selectedAmount, selectedTerm, selectedRate, loanType } = location.state;
            
            
            // Only set term and rate if they're not N/A
            setFormData(prev => ({
                ...prev,
                // Only set loanTerm if selectedTerm exists and isn't N/A
                loanTerm: selectedTerm && selectedTerm !== 'N/A' ? selectedTerm.toString().split(' ')[0] : '',
                // Only set interestRate if selectedRate exists and isn't N/A
                interestRate: selectedRate && selectedRate !== 'N/A' ? selectedRate.toString().replace('%', '') : '',
                // Set minimum amount for MAX Signature
                requestedAmount: loanType === 'MAX Signature Application' ? '5000' : (selectedAmount ? selectedAmount.toString() : ''),
                loan_type: loanType || 'Personal Loan'
            }));
            
            // Set loan type display based on the loan type
            if (loanType) {
                setLoanTypeDisplay(loanType.replace(' Application', ''));
            }
        }

        // Handle query parameters when editing from review page
        const params = new URLSearchParams(location.search);
        if (params.toString()) {
            const formDataFromParams = {};
            params.forEach((value, key) => {
                // Convert string 'true'/'false' to boolean for checkboxes
                if (value === 'true' || value === 'false') {
                    formDataFromParams[key] = value === 'true';
                } else {
                    formDataFromParams[key] = value;
                }
            });
            setFormData(prev => ({
                ...prev,
                ...formDataFromParams
            }));
            setIsReviewing(true);
        }
    }, [location]);

    // Add scroll to top effect
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'ssn') {
            // Remove all non-digits
            const digitsOnly = value.replace(/\D/g, '');
            // Format as XXX-XX-XXXX
            let formattedSSN = digitsOnly;
            if (digitsOnly.length > 0) {
                formattedSSN = digitsOnly.replace(/(\d{3})(\d{1,2})?(\d{1,4})?/, (_, p1, p2, p3) => {
                    let result = p1;
                    if (p2) result += '-' + p2;
                    if (p3) result += '-' + p3;
                    return result;
                });
            }
            // Limit to XXX-XX-XXXX format
            formattedSSN = formattedSSN.slice(0, 11);
            
            setFormData(prev => ({
                ...prev,
                [name]: formattedSSN
            }));
        } else if (name === 'homePhone' || name === 'mobilePhone' || name === 'workPhone') {
            // Remove all non-digits
            const digitsOnly = value.replace(/\D/g, '');
            // Format as XXX-XXX-XXXX
            let formattedPhone = digitsOnly;
            if (digitsOnly.length > 0) {
                formattedPhone = digitsOnly.replace(/(\d{3})(\d{1,3})?(\d{1,4})?/, (_, p1, p2, p3) => {
                    let result = p1;
                    if (p2) result += '-' + p2;
                    if (p3) result += '-' + p3;
                    return result;
                });
            }
            // Limit to XXX-XXX-XXXX format
            formattedPhone = formattedPhone.slice(0, 12);
            
            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        // For MAX Signature, enforce minimum of 5000
        if (formData.loan_type === 'MAX Signature Application') {
            const numValue = parseInt(value);
            if (numValue < 5000) {
                alert('Minimum credit limit for MAX Signature is $5,000');
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            requestedAmount: value
        }));
    };

    const validatePage = () => {
        const requiredFields = {
            1: ['firstName', 'lastName', 'birthDate', 'ssn'],
            2: ['addressLine1', 'city', 'state', 'zipCode'],
            3: ['housing', 'housingPayment', 'homePhone', 'email', 'preferredContact'],
            4: ['incomeSource', 'occupation', 'incomeFrequency', 'incomeAmount', 'employmentYears', 'employmentMonths']
        };

        // Add unsecured loan purpose validation if it's an unsecured loan
        if (formData.loan_type === 'Unsecured Loan Application' && !formData.unsecuredLoanPurpose) {
            alert('Please select a loan purpose');
            return false;
        }
        
        const missing = requiredFields[currentPage].filter(field => !formData[field]);
        if (missing.length) {
            alert(`Please complete: ${missing.join(', ')}`);
            return false;
        }
        return true;
    };

    const nextPage = () => {
        if (validatePage()) {
            setCurrentPage(prev => Math.min(prev + 1, totalPages));
        }
    };

    const previousPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 1));
    };

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        

        if (!validatePage()) {
            
            return;
        }

        const submissionData = {
            ...formData,
            loanTerm: formData.loanTerm,
            interestRate: formData.interestRate,
            monthlyPayment: formData.monthlyPayment,
            requestedAmount: formData.requestedAmount
        };

        

        try {
            const queryParams = new URLSearchParams();
            Object.entries(submissionData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    queryParams.append(key, value.toString());
                }
            });
            
            const queryString = queryParams.toString();
            

            navigate('/review?' + queryString);
        } catch (error) {
            
        }
    };

    const renderProgressBar = () => {
        const progress = (currentPage / totalPages) * 100;
        return (
            <div className="progress-container">
                <div className="progress-bar">
                    <div className="progress" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="page-indicator">Step {currentPage} of {totalPages}</div>
            </div>
        );
    };

    const renderPageSummary = (pageNumber) => {
        switch(pageNumber) {
            case 1:
                return (
                    <div className="page-summary" onClick={() => setCurrentPage(1)}>
                        <h4>Personal Information</h4>
                        <p>{formData.firstName} {formData.lastName}</p>
                    </div>
                );
            case 2:
                return (
                    <div className="page-summary" onClick={() => setCurrentPage(2)}>
                        <h4>Address</h4>
                        <p>{formData.addressLine1}, {formData.city}, {formData.state}</p>
                    </div>
                );
            case 3:
                return (
                    <div className="page-summary" onClick={() => setCurrentPage(3)}>
                        <h4>Contact & Housing</h4>
                        <p>{formData.email} | {formData.housing}</p>
                    </div>
                );
            case 4:
                return (
                    <div className="page-summary" onClick={() => setCurrentPage(4)}>
                        <h4>Income</h4>
                        <p>{formData.incomeSource} | ${formData.incomeAmount}</p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="auto-application-container">
            <div>
                <Header 
                    name="My Offers"
                    variant="primary"
                    fullWidth={true}
                    elevation={2}
                    color="white"
                />
            </div>
            <div className="auto-application-page">
                <SideMenu menuItems={menuItems} />
                {isCollapsed && <TopMenu />}
                <div className="main-content">
                    <div className="container">
                        <div className="loan-details-container">
                            <h2>{loanTypeDisplay} Application Loan Details</h2>
                            {formData.loanTerm && formData.interestRate && (
                                <>
                                    <p>Selected Term: <span>{formData.loanTerm} months</span></p>
                                    <p>Selected Rate: <span>{formData.interestRate}%</span></p>
                                </>
                            )}
                            <div className="form-group">
                                <p>Requested Amount: 
                                    <span className="amount-input-container">
                                        $<input
                                            type="number"
                                            value={formData.requestedAmount}
                                            onChange={handleAmountChange}
                                            placeholder="Enter amount"
                                            className="amount-input"
                                            min={formData.loan_type === 'MAX Signature Application' ? "5000" : "0"}
                                        />
                                    </span>
                                </p>
                                <div className="validation-message" style={{ textAlign: 'right' }}>
                                    {(!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) && 'Please enter a valid loan amount greater than $0'}
                                </div>
                            </div>
                            {formData.loan_type === 'Unsecured Loan Application' && (
                                <div className="form-group">
                                    <select className="custom-select" name="unsecuredLoanPurpose" value={formData.unsecuredLoanPurpose} onChange={handleInputChange} required>
                                        <option value="">Select Loan Purpose</option>
                                        <option value="Consolidation">Consolidation</option>
                                        <option value="For relatives">For relatives</option>
                                        <option value="Holiday">Holiday</option>
                                        <option value="Home repair">Home repair</option>
                                        <option value="Household goods">Household goods</option>
                                        <option value="Legal">Legal</option>
                                        <option value="Medical">Medical</option>
                                        <option value="Other">Other</option>
                                        <option value="PC system">PC system</option>
                                        <option value="Taxes">Taxes</option>
                                        <option value="Vacation">Vacation</option>
                                        <option value="Wedding">Wedding</option>
                                    </select>
                                    <div className="validation-message">{!formData.unsecuredLoanPurpose && 'Loan purpose is required'}</div>
                                </div>
                            )}
                        </div>

                        {renderProgressBar()}

                        {isReviewing && (
                            <div className="review-summary">
                                <h3>Application Summary</h3>
                                <p>Click any section to edit</p>
                                {[1, 2, 3, 4].map(pageNum => renderPageSummary(pageNum))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="application-form">
                            {/* Page 1 */}
                            <div className={`page ${currentPage === 1 ? 'active' : ''}`}>
                                <h3>Personal Information</h3>
                                <div className="form-group">
                                    <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.firstName && 'First Name is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="text" name="middleName" placeholder="Middle Name" value={formData.middleName} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.lastName && 'Last Name is required'}</div>
                                </div>
                                <div className="form-group">
                                    <select className="custom-select" name="suffix" value={formData.suffix} onChange={handleInputChange}>
                                        <option value="">Select Suffix</option>
                                        <option value="Jr">Jr</option>
                                        <option value="Sr">Sr</option>
                                        <option value="II">II</option>
                                        <option value="III">III</option>
                                        <option value="IV">IV</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <input type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.birthDate && 'Birth Date is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="text" name="ssn" placeholder="SSN" value={formData.ssn} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.ssn && 'SSN is required'}</div>
                                </div>
                            </div>

                            {/* Page 2 */}
                            <div className={`page ${currentPage === 2 ? 'active' : ''}`}>
                                <h3>Address Information</h3>
                                <div className="form-group">
                                    <input type="text" name="addressLine1" placeholder="Address Line 1" value={formData.addressLine1} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.addressLine1 && 'Address is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="text" name="addressLine2" placeholder="Address Line 2" value={formData.addressLine2} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.city && 'City is required'}</div>
                                </div>
                                <div className="form-group">
                                    <select className="custom-select" name="state" value={formData.state} onChange={handleInputChange} required>
                                        <option value="">Select State</option>
                                        {[
                                            "Alabama", "Alaska", "APO/FPO (ZIP 090xx - 098xx)", "APO/FPO (ZIP 340xx)", "APO/FPO (ZIP 962xx - 966xx)",
                                            "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia",
                                            "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
                                            "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
                                            "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
                                            "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
                                            "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
                                            "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming", "Puerto Rico",
                                            "American Samoa", "Micronesia", "Guam", "Marshall Islands", "Northern Mariana Islands",
                                            "Palau", "U.S. Minor Outlying Islands", "U.S. Virgin Islands"
                                        ].map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                    <div className="validation-message">{!formData.state && 'State is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="text" name="zipCode" placeholder="Zip Code" value={formData.zipCode} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.zipCode && 'Zip Code is required'}</div>
                                </div>
                            </div>

                            {/* Page 3 */}
                            <div className={`page ${currentPage === 3 ? 'active' : ''}`}>
                                <h3>Housing Information</h3>
                                <div className="form-group">
                                    <select className="custom-select" name="housing" value={formData.housing} onChange={handleInputChange} required>
                                        <option value="">Select Housing Type</option>
                                        <option value="Buy/Own with Mortgage">Buy/Own with Mortgage</option>
                                        <option value="Government Quarters">Government Quarters</option>
                                        <option value="Living with Others">Living with Others</option>
                                        <option value="Other">Other</option>
                                        <option value="Own - Free & Clear">Own - Free & Clear</option>
                                        <option value="Rent">Rent</option>
                                    </select>
                                    <div className="validation-message">{!formData.housing && 'Housing type is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="number" name="housingPayment" placeholder="Monthly Payment" value={formData.housingPayment} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.housingPayment && 'Monthly payment is required'}</div>
                                </div>
                                <h3>Contact Information</h3>
                                <div className="form-group">
                                    <input type="tel" name="homePhone" placeholder="Home Phone" value={formData.homePhone} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.homePhone && 'Home phone is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="tel" name="mobilePhone" placeholder="Mobile Phone" value={formData.mobilePhone} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <input type="tel" name="workPhone" placeholder="Work Phone" value={formData.workPhone} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.email && 'Email is required'}</div>
                                </div>
                                <div className="form-group">
                                    <select className="custom-select" name="preferredContact" value={formData.preferredContact} onChange={handleInputChange} required>
                                        <option value="">Preferred Contact Method</option>
                                        <option value="Call Home Phone">Call Home Phone</option>
                                        <option value="Call Mobile Phone">Call Mobile Phone</option>
                                        <option value="Call Work Phone">Call Work Phone</option>
                                        <option value="Text Mobile">Text Mobile</option>
                                        <option value="Email">Email</option>
                                    </select>
                                    <div className="validation-message">{!formData.preferredContact && 'Preferred contact method is required'}</div>
                                </div>
                            </div>

                            {/* Page 4 */}
                            <div className={`page ${currentPage === 4 ? 'active' : ''}`}>
                                <h3>Income Information</h3>
                                <div className="form-group">
                                    <input type="text" name="incomeSource" placeholder="Employer/Source" value={formData.incomeSource} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.incomeSource && 'Employer/Source is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="text" name="occupation" placeholder="Occupation" value={formData.occupation} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.occupation && 'Occupation is required'}</div>
                                </div>
                                <div className="form-group">
                                    <select className="custom-select" name="incomeFrequency" value={formData.incomeFrequency} onChange={handleInputChange} required>
                                        <option value="">Select Pay Frequency</option>
                                        <option value="Annually">Annually</option>
                                        <option value="Quarterly">Quarterly</option>
                                        <option value="Bi-Monthly">Bi-Monthly</option>
                                        <option value="Monthly">Monthly</option>
                                        <option value="Bi-Weekly">Bi-Weekly</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Hourly">Hourly</option>
                                    </select>
                                    <div className="validation-message">{!formData.incomeFrequency && 'Pay frequency is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="number" name="incomeAmount" placeholder="Income Amount" value={formData.incomeAmount} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.incomeAmount && 'Income amount is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="number" name="employmentYears" placeholder="Employment Years" value={formData.employmentYears} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.employmentYears && 'Employment years is required'}</div>
                                </div>
                                <div className="form-group">
                                    <input type="number" name="employmentMonths" placeholder="Employment Months" value={formData.employmentMonths} onChange={handleInputChange} required />
                                    <div className="validation-message">{!formData.employmentMonths && 'Employment months is required'}</div>
                                </div>
                                <details className="additional-income">
                                    <summary>Additional Income</summary>
                                    <div className="form-group">
                                        <input type="text" name="additionalIncomeSource" placeholder="Additional Source" value={formData.additionalIncomeSource} onChange={handleInputChange} />
                                    </div>
                                    <div className="form-group">
                                        <select className="custom-select" name="additionalIncomeType" value={formData.additionalIncomeType} onChange={handleInputChange}>
                                            <option value="">Select Type</option>
                                            <option value="Previous Income">Previous Income</option>
                                            <option value="Additional Income">Additional Income</option>
                                            <option value="Alimony">Alimony</option>
                                            <option value="Child Support">Child Support</option>
                                            <option value="Retirement">Retirement</option>
                                            <option value="Social Security">Social Security</option>
                                            <option value="Other">Other</option>
                                            <option value="Unemployed">Unemployed</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <select className="custom-select" name="additionalIncomeFrequency" value={formData.additionalIncomeFrequency} onChange={handleInputChange}>
                                            <option value="">Select Frequency</option>
                                            <option value="Annual">Annual</option>
                                            <option value="Quarterly">Quarterly</option>
                                            <option value="Bi-Monthly">Bi-Monthly</option>
                                            <option value="Monthly">Monthly</option>
                                            <option value="Bi-Weekly">Bi-Weekly</option>
                                            <option value="Weekly">Weekly</option>
                                            <option value="Hourly">Hourly</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <input type="number" name="additionalIncomeAmount" placeholder="Amount" value={formData.additionalIncomeAmount} onChange={handleInputChange} />
                                    </div>
                                </details>
                            </div>

                            <div className="nav-buttons">
                                {currentPage > 1 && (
                                    <button type="button" className="btn-secondary" onClick={previousPage}>
                                        Previous
                                    </button>
                                )}
                                {currentPage < totalPages ? (
                                    <button type="button" className="apply-btn" onClick={nextPage}>
                                        Next
                                    </button>
                                ) : (
                                    <button 
                                        type="submit" 
                                        className="apply-btn"
                                        onClick={(e) => {
                                            
                                            handleSubmit(e);
                                        }}
                                    >
                                        {isReviewing ? 'Update Application' : 'Review Application'}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoanApplication;